import {verifiedFetch} from '@github-ui/verified-fetch'
import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useEffect, useRef, useState} from 'react'
import styles from './PullRequestSidebarNudge.module.css'

export interface PullRequestSidebarNudgeAnalytics {
  category: string
  group: string
  location: string
  ctaAction: string
  organizationId?: number | string
  plan?: string
}

export interface PullRequestSidebarNudgeProps {
  title: string
  description: string
  imagePathLight: string
  imagePathDark: string
  imageAltLight?: string
  imageAltDark?: string
  ctaTitle: string
  ctaHref: string
  dismissPath: string
  analytics: PullRequestSidebarNudgeAnalytics
  testSelector: string
  useDismissAndRedirect?: boolean
}

export function PullRequestSidebarNudge({
  title,
  description,
  imagePathLight,
  imagePathDark,
  imageAltLight = '',
  imageAltDark = '',
  ctaTitle,
  ctaHref,
  dismissPath,
  analytics,
  testSelector,
  useDismissAndRedirect = false,
}: PullRequestSidebarNudgeProps) {
  const [hidden, setHidden] = useState(false)
  const dismissAnnouncementRef = useRef<HTMLSpanElement>(null)

  const handleDismiss = async () => {
    try {
      await verifiedFetch(dismissPath, {method: 'POST'})
    } catch {
      // Hide the card even if the server didn't record the dismiss.
    } finally {
      setHidden(true)
    }
  }

  const handleCtaClick = () => {
    if (!useDismissAndRedirect) return
    const dismissAndForget = async () => {
      try {
        await verifiedFetch(dismissPath, {method: 'POST', keepalive: true})
      } catch {
        // Browser still follows the CTA href.
      }
    }
    void dismissAndForget()
  }

  // Move focus to the live region so dismissal isn't silent for AT/keyboard
  // users and focus doesn't collapse to <body> (WCAG 2.4.3).
  useEffect(() => {
    if (hidden) {
      dismissAnnouncementRef.current?.focus()
    }
  }, [hidden])

  if (hidden) {
    return (
      <span
        ref={dismissAnnouncementRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className={styles.dismissAnnouncement}
      >
        Nudge dismissed.
      </span>
    )
  }

  const analyticsLabel = `group:${analytics.group};location:${analytics.location}`

  return (
    <>
      <span data-test-selector={testSelector} />
      <div
        className={styles.container}
        data-hpc
        data-testid="pull-request-sidebar-nudge"
        data-analytics-visible={JSON.stringify({
          category: analytics.category,
          action: 'visible',
          label: analyticsLabel,
          ...(analytics.organizationId !== undefined && {organizationId: analytics.organizationId}),
          ...(analytics.plan !== undefined && {plan: analytics.plan}),
        })}
      >
        <div className={styles.dismiss}>
          <IconButton
            icon={XIcon}
            variant="invisible"
            aria-label="Dismiss"
            onClick={handleDismiss}
            data-analytics-event={JSON.stringify({
              category: analytics.category,
              action: 'dismiss',
              label: analyticsLabel,
            })}
          />
        </div>

        <div className={styles.imageContainer}>
          <img src={imagePathLight} alt={imageAltLight} className={`${styles.image} ${styles.imageLight}`} />
          <img src={imagePathDark} alt={imageAltDark} className={`${styles.image} ${styles.imageDark}`} />
        </div>

        <div className={styles.main}>
          <h3 className={styles.title}>{title}</h3>
          <div className={styles.description}>{description}</div>

          <a
            href={ctaHref}
            onClick={handleCtaClick}
            className={`${styles.cta} btn btn-sm btn-primary text-small px-2`}
            data-test-selector={`${testSelector}-cta`}
            data-analytics-event={JSON.stringify({
              category: analytics.category,
              action: analytics.ctaAction,
              label: analyticsLabel,
            })}
          >
            {ctaTitle}
          </a>
        </div>
      </div>
    </>
  )
}
